import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuEllipsisVertical, LuLogIn, LuPlus, LuSettings } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Action, type GUID, type InternalUserProfile, type InternalUserProfileRequest, Role } from '@ndvr/auth-api';

import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useInternalUserProfilesQuery } from '@/modules/internal-user-queries';
import { getPersonName } from 'utils';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useLoginAsCommandCenterUser } from 'views/person/person-queries';
import { MailTo } from 'widgets';

import {
    type InternalUserTenantPermission,
    useCreateInternalUserProfileMutation,
    useInternalUserTenantPermissionsQuery,
} from './internal-user-profile-queries';
import { useActiveRoles } from './role-queries';

type InternalUserProfileFormValues = {
    email: string;
    firstName: string;
    lastName: string;
};

const emptyProfileValues: InternalUserProfileFormValues = {
    email: '',
    firstName: '',
    lastName: '',
};

const buildProfileRequest = (values: InternalUserProfileFormValues): InternalUserProfileRequest => {
    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();

    return {
        email: values.email.trim(),
        firstName: firstName || undefined,
        lastName: lastName || undefined,
    };
};

const compareInternalUserProfiles = (a: InternalUserProfile, b: InternalUserProfile) => {
    const nameComparison = getPersonName(a).localeCompare(getPersonName(b), undefined, { sensitivity: 'base' });
    if (nameComparison !== 0) {
        return nameComparison;
    }

    return a.email.localeCompare(b.email, undefined, { sensitivity: 'base' });
};

export const InternalUserProfilesList = () => {
    const { data: internalUsers = [], isLoading, isError, refetch } = useInternalUserProfilesQuery();
    const createProfileMutation = useCreateInternalUserProfileMutation();
    const { mutate: loginAs, isPending: isLoginPending } = useLoginAsCommandCenterUser();
    const { data: hasRoleAdmin = false } = useUserHasRole(Role.ROLE_ADMIN);
    const { data: hasImpersonation = false } = useUserHasRole(Role.IMPERSONATION);
    const navigate = useNavigate();

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const createForm = useForm<InternalUserProfileFormValues>({
        mode: 'onChange',
        defaultValues: emptyProfileValues,
    });

    const sortedProfiles = useMemo(() => {
        const activeProfiles = internalUsers.filter((profile) => !profile.archived);
        return [...activeProfiles].sort(compareInternalUserProfiles);
    }, [internalUsers]);

    const filteredProfiles = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();

        if (!normalizedQuery) {
            return sortedProfiles;
        }

        return sortedProfiles.filter((profile) => {
            const fullName = getPersonName(profile).toLowerCase();
            const firstName = profile.firstName ?? '';
            const lastName = profile.lastName ?? '';
            const email = profile.email ?? '';
            const reverseName = `${lastName} ${firstName}`.trim().toLowerCase();
            const normalizedEmail = email.trim().toLowerCase();

            return (
                fullName.includes(normalizedQuery) ||
                reverseName.includes(normalizedQuery) ||
                normalizedEmail.includes(normalizedQuery) ||
                profile.profileGUID.toLowerCase().includes(normalizedQuery)
            );
        });
    }, [searchQuery, sortedProfiles]);

    const handleCloseCreateDialog = () => {
        setIsCreateDialogOpen(false);
        createForm.reset(emptyProfileValues);
    };

    const handleCreateProfile = createForm.handleSubmit((values) => {
        createProfileMutation.mutate(buildProfileRequest(values), {
            onSuccess: handleCloseCreateDialog,
        });
    });

    return (
        <>
            <Card>
                <CardHeader className="flex flex-row justify-between pt-6 pb-0">
                    <CardTitle className="font-heading font-medium">Claro Intelligent Hub Users</CardTitle>
                    <div className="flex items-center gap-3">
                        <Input
                            placeholder="Search by name, email or GUID..."
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            className="w-64"
                        />
                        <Button onClick={() => setIsCreateDialogOpen(true)}>
                            <LuPlus className="mr-2 h-4 w-4" />
                            Create Internal User
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="text-muted-foreground py-6 text-center">Loading internal users...</div>
                    ) : isError ? (
                        <div className="text-destructive py-6 text-center text-sm">
                            Failed to load internal users.
                            <Button variant="link" onClick={() => refetch()} className="ml-2 p-0">
                                Retry
                            </Button>
                        </div>
                    ) : sortedProfiles.length > 0 ? (
                        <>
                            {filteredProfiles.length > 0 ? (
                                <Table className="w-full table-fixed border-collapse border-spacing-0">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[22%]">Name</TableHead>
                                            <TableHead className={hasRoleAdmin ? 'w-[38%]' : undefined}>
                                                Tenants
                                            </TableHead>
                                            {hasRoleAdmin && <TableHead>Roles</TableHead>}
                                            <TableHead className="w-24 text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredProfiles.map((profile) => (
                                            <TableRow
                                                key={profile.profileGUID}
                                                className="cursor-pointer font-normal"
                                                onClick={() => navigate(`/admin/internal-users/${profile.profileGUID}`)}
                                            >
                                                <TableCell className="break-words">
                                                    <div className="flex flex-col">
                                                        <div>{getPersonName(profile)}</div>
                                                        <div onClick={(event) => event.stopPropagation()}>
                                                            <MailTo address={profile.email} />
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <InternalUserTenantsCell internalUserGUID={profile.profileGUID} />
                                                </TableCell>
                                                {hasRoleAdmin && (
                                                    <TableCell className="break-words whitespace-normal">
                                                        <InternalUserRolesCell internalUserGUID={profile.profileGUID} />
                                                    </TableCell>
                                                )}
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger
                                                                onClick={(event) => event.stopPropagation()}
                                                            >
                                                                <div className="hover:bg-ndvr-seafoam/50 hover:text-ndvr-dark inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium">
                                                                    <LuEllipsisVertical className="h-4 w-4" />
                                                                    <span className="sr-only">Open menu</span>
                                                                </div>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent
                                                                align="end"
                                                                onClick={(event) => event.stopPropagation()}
                                                            >
                                                                <CopyGuidDropdownMenuItem guid={profile.profileGUID} />
                                                                <DropdownMenuItem
                                                                    className="flex w-full items-center gap-2"
                                                                    onClick={() =>
                                                                        navigate(
                                                                            `/admin/internal-users/${profile.profileGUID}`,
                                                                        )
                                                                    }
                                                                >
                                                                    <span>
                                                                        <LuSettings className="h-4 w-4" />
                                                                    </span>
                                                                    <span>Edit</span>
                                                                </DropdownMenuItem>
                                                                {hasImpersonation && (
                                                                    <DropdownMenuItem
                                                                        className="flex w-full items-center gap-2"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            loginAs(profile.profileGUID);
                                                                        }}
                                                                        disabled={isLoginPending}
                                                                    >
                                                                        <span>
                                                                            <LuLogIn className="h-4 w-4" />
                                                                        </span>
                                                                        <span>Login As</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="text-muted-foreground py-6 text-center">
                                    No internal users match your search.
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-muted-foreground py-6 text-center">No internal users found.</div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => !open && handleCloseCreateDialog()}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create Internal User</DialogTitle>
                        <DialogDescription>Add a new internal user profile.</DialogDescription>
                    </DialogHeader>
                    <Form {...createForm}>
                        <form onSubmit={handleCreateProfile} className="flex flex-col gap-4">
                            <FormField
                                control={createForm.control}
                                name="email"
                                rules={{ required: 'Email is required' }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type="email"
                                                placeholder="name@ndvr.com"
                                                disabled={createProfileMutation.isPending}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={createForm.control}
                                name="firstName"
                                rules={{ required: 'First name is required' }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>First Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                placeholder="First name"
                                                disabled={createProfileMutation.isPending}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={createForm.control}
                                name="lastName"
                                rules={{ required: 'Last name is required' }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Last Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                placeholder="Last name"
                                                disabled={createProfileMutation.isPending}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    type="button"
                                    onClick={handleCloseCreateDialog}
                                    disabled={createProfileMutation.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        createProfileMutation.isPending ||
                                        !createForm.watch('email')?.trim() ||
                                        !createForm.watch('firstName')?.trim() ||
                                        !createForm.watch('lastName')?.trim()
                                    }
                                >
                                    {createProfileMutation.isPending ? 'Creating...' : 'Create User'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        </>
    );
};

const InternalUserTenantsCell = ({ internalUserGUID }: { internalUserGUID?: GUID<InternalUserProfile> }) => {
    const {
        data: tenantPermissions = [],
        isLoading,
        isError,
    } = useInternalUserTenantPermissionsQuery(internalUserGUID);

    const tenantPermissionsWithAccess = useMemo(() => {
        return tenantPermissions
            .filter((permission) => hasTenantAccess(permission))
            .sort((a, b) => a.tenant.name.localeCompare(b.tenant.name));
    }, [tenantPermissions]);

    if (!internalUserGUID) {
        return <div className="text-muted-foreground text-sm">-</div>;
    }

    if (isLoading) {
        return <div className="text-muted-foreground text-sm">Loading...</div>;
    }

    if (isError) {
        return <div className="text-destructive text-sm">Failed to load.</div>;
    }

    if (tenantPermissionsWithAccess.length === 0) {
        return <div className="text-muted-foreground text-sm">No access</div>;
    }

    const tenantNames = tenantPermissionsWithAccess.map((permission) => permission.tenant.name).join(', ');

    return <div>{tenantNames}</div>;
};

const hasTenantAccess = (permission: InternalUserTenantPermission) =>
    permission.action === Action.READ ||
    permission.action === Action.WRITE ||
    permission.action === Action.FULL_CONTROL;

const InternalUserRolesCell = ({ internalUserGUID }: { internalUserGUID: GUID<InternalUserProfile> }) => {
    const { data: activeRoles = [], isLoading, isError } = useActiveRoles(internalUserGUID);

    if (isLoading) {
        return <div className="text-muted-foreground text-sm">Loading...</div>;
    }

    if (isError) {
        return <div className="text-destructive text-sm">Failed to load.</div>;
    }

    const roles = Array.from(new Set(activeRoles.map((role) => role.role)));

    if (roles.length === 0) {
        return <div className="text-muted-foreground text-sm">No roles</div>;
    }

    return <div>{roles.join(', ')}</div>;
};
